import { ANNUAL_MONTHS_CHARGED, SECURE_LAUNCH_LITE_MONTHLY_LAUNCHES, type LaunchTier } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Icon } from "@sagot/ui";
import type { ReactNode } from "react";
import { useFormatters } from "../shared";
import { TableScroll } from "./table-scroll";

const K = "billing.addons.compare";

export interface ComparePrice {
  /** Monthly list price in the billing currency (a decimal string), `null` while the catalogue has not priced it. */
  readonly monthly: string | null;
  readonly currency: string;
}

interface SecureLaunchCompareProps {
  lite: ComparePrice;
  plus: ComparePrice;
  /** The tier the workspace has now (its column says so); `none` marks neither column. */
  current: LaunchTier;
}

function Mark({ included }: { included: boolean }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon name={included ? "check" : "x"} className={included ? "size-4 text-success" : "size-4 text-fg-subtle"} />
      <span className={included ? "" : "text-fg-muted"}>{t(included ? `${K}.included` : `${K}.notIncluded`)}</span>
    </span>
  );
}

function Row({ label, hint, lite, plus }: { label: string; hint?: string; lite: ReactNode; plus: ReactNode }) {
  return (
    <tr>
      <th scope="row" className="min-w-40 font-semibold">
        {label}
        {hint ? <span className="mt-0.5 block text-xs font-normal text-fg-subtle">{hint}</span> : null}
      </th>
      <td>{lite}</td>
      <td>{plus}</td>
    </tr>
  );
}

/**
 * Secure launch Lite next to Plus (DEC-151): what each costs by the month and the year, what each includes and the launch volume.
 * Prices come from the add-on catalogue in the billing currency; the year is the monthly price times the months charged.
 */
export function SecureLaunchCompare({ lite, plus, current }: SecureLaunchCompareProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const month = (price: ComparePrice) => (price.monthly === null ? "—" : fmt.money(Number(price.monthly), price.currency));
  const year = (price: ComparePrice) => (price.monthly === null ? "—" : fmt.money(Number(price.monthly) * ANNUAL_MONTHS_CHARGED, price.currency));
  const head = (tier: "lite" | "plus") => (
    <th scope="col" className="min-w-32">
      <span className="inline-flex flex-wrap items-center gap-2">
        {t(`${K}.col_${tier}`)}
        {current === tier ? <Badge tone="brand">{t(`${K}.current`)}</Badge> : null}
      </span>
    </th>
  );
  return (
    <div className="space-y-2" data-testid="secure-launch-compare">
      <div>
        <h3 className="text-sm font-bold">{t(`${K}.title`)}</h3>
        <p className="mt-0.5 text-xs text-fg-subtle">{t(`${K}.subtitle`)}</p>
      </div>
      <TableScroll label={t(`${K}.title`)}>
        <table className="table w-full text-sm">
          <caption className="sr-only">{t(`${K}.title`)}</caption>
          <thead>
            <tr>
              <th scope="col">{t(`${K}.col_what`)}</th>
              {head("lite")}
              {head("plus")}
            </tr>
          </thead>
          <tbody>
            <Row label={t(`${K}.row_month`)} lite={<strong className="tabular-nums">{month(lite)}</strong>} plus={<strong className="tabular-nums">{month(plus)}</strong>} />
            <Row label={t(`${K}.row_year`)} hint={t(`${K}.yearHint`)} lite={<span className="tabular-nums">{year(lite)}</span>} plus={<span className="tabular-nums">{year(plus)}</span>} />
            <Row label={t(`${K}.row_launch`)} hint={t(`${K}.launchHint`)} lite={<Mark included />} plus={<Mark included />} />
            <Row label={t(`${K}.row_named`)} hint={t(`${K}.namedHint`)} lite={<Mark included />} plus={<Mark included />} />
            <Row label={t(`${K}.row_facts`)} hint={t(`${K}.factsHint`)} lite={<Mark included={false} />} plus={<Mark included />} />
            <Row
              label={t(`${K}.row_volume`)}
              lite={<span className="tabular-nums">{t(`${K}.volumeLite`, { limit: fmt.number(SECURE_LAUNCH_LITE_MONTHLY_LAUNCHES) })}</span>}
              plus={t(`${K}.volumePlus`)}
            />
            <Row label={t(`${K}.row_plans`)} lite={t(`${K}.plansBoth`)} plus={t(`${K}.plansBoth`)} />
          </tbody>
        </table>
      </TableScroll>
      <p className="text-xs text-fg-subtle">{t(`${K}.footnote`)}</p>
    </div>
  );
}
